import { format, parseISO } from "date-fns";
import {
    BankReconciliation,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import {
    getStatmentTransactionId,
    pairHasDifference,
} from "@common/helpers/reconciliation";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { FinancialAccount } from "@common/types/financialAccount";

export enum ReconciliationDateUpdateReason {
    MATCHED_WITH_STATMENT = "matched_with_statment",
}

export const RECONCILIATION_PRIMARY_HOTKEY = "mod+enter";

export interface ReconciliationShellProps {
    show: boolean;
    reconciliedFinancialAccount: FinancialAccount;
    previousReconciliation?: BankReconciliation;
    isLoadingLastReconciliation?: boolean;
    onHide: () => void;
    onSuccess?: () => void;
}

export type ReconciliationReviewMode =
    | { kind: "workflow-review" }
    | { kind: "workflow-match"; onContinue: () => void };

export function countSuggestedActions(
    pairs: TransactionsPairForReconciliation[],
): number {
    return pairs.filter((pair) => pairHasDifference(pair)).length;
}

export function getReconciliationTitle(
    financialAccount: FinancialAccount,
): string {
    return `Reconcile ${financialAccount.institutionName ?? financialAccount.name} ${financialAccount.accountNumberMask ? getAnonymousAccountNumber(financialAccount.accountNumberMask, 2) : ""}`;
}

interface GetLastReconciledLabelParams {
    previousReconciliation?: BankReconciliation;
    isLoadingLastReconciliation?: boolean;
}

export function getLastReconciledLabel({
    previousReconciliation,
    isLoadingLastReconciliation,
}: GetLastReconciledLabelParams): string | undefined {
    if (isLoadingLastReconciliation) {
        return undefined;
    }
    if (previousReconciliation) {
        return `Previously reconciled up to ${format(parseISO(previousReconciliation.closingDate), fullDateFullYearFormat)}`;
    }
    return "Never reconciled";
}

interface PairDifferenceIsAcceptedParams {
    pair: TransactionsPairForReconciliation;
    financialTransactionsToRemoveIdsSet: Set<number>;
    statementTransactionsToAddIdsSet: Set<string>;
    notReconciledFinancialTransactionsIdsSet: Set<number>;
    financialTransactionsFromPreviousPeriodIds: number[];
}

export function pairDifferenceIsAccepted({
    pair,
    financialTransactionsToRemoveIdsSet,
    statementTransactionsToAddIdsSet,
    notReconciledFinancialTransactionsIdsSet,
    financialTransactionsFromPreviousPeriodIds,
}: PairDifferenceIsAcceptedParams): boolean {
    if (pair.fromAccount) {
        return (
            financialTransactionsToRemoveIdsSet.has(
                pair.fromAccount.financialTransactionId,
            ) ||
            notReconciledFinancialTransactionsIdsSet.has(
                pair.fromAccount.financialTransactionId,
            ) ||
            financialTransactionsFromPreviousPeriodIds.includes(
                pair.fromAccount.financialTransactionId,
            )
        );
    }
    if (pair.fromStatement) {
        return statementTransactionsToAddIdsSet.has(
            getStatmentTransactionId(pair.fromStatement),
        );
    }
    return false;
}

interface BuildReconciliationTableSectionsParams {
    pairs: TransactionsPairForReconciliation[];
    openingDate: string;
    closingDate: string;
    financialTransactionsFromPreviousPeriodIds: number[];
}

interface ReconciliationTableSections {
    flatPairs: TransactionsPairForReconciliation[];
    lastBeforeStatementIndex?: number;
    lastInStatementIndex?: number;
}

export function buildReconciliationTableSections({
    pairs,
    openingDate,
    closingDate,
    financialTransactionsFromPreviousPeriodIds,
}: BuildReconciliationTableSectionsParams): ReconciliationTableSections {
    const previousPeriodIds = new Set(
        financialTransactionsFromPreviousPeriodIds,
    );
    const isFromPreviousPeriod = (
        pair: TransactionsPairForReconciliation,
    ): boolean =>
        pair.fromAccount != null &&
        previousPeriodIds.has(pair.fromAccount.financialTransactionId);
    const pairDate = (pair: TransactionsPairForReconciliation) =>
        pair.fromStatement?.date ?? pair.fromAccount?.date;
    const priorToOpeningDate = pairs.filter((pair) => {
        const date = pairDate(pair);
        return (
            !isFromPreviousPeriod(pair) &&
            date !== undefined &&
            date < openingDate
        );
    });
    const markedFromPreviousPeriod = pairs.filter(isFromPreviousPeriod);
    const withinStatementsDates = pairs.filter((pair) => {
        const date = pairDate(pair);
        return (
            !isFromPreviousPeriod(pair) &&
            date !== undefined &&
            date >= openingDate &&
            date <= closingDate
        );
    });
    const afterClosingDate = pairs.filter((pair) => {
        const date = pairDate(pair);
        return (
            !isFromPreviousPeriod(pair) &&
            date !== undefined &&
            date > closingDate
        );
    });
    const flatPairs = [
        ...priorToOpeningDate,
        ...markedFromPreviousPeriod,
        ...withinStatementsDates,
        ...afterClosingDate,
    ];
    const beforeStatementCount =
        priorToOpeningDate.length + markedFromPreviousPeriod.length;
    const withinStatementEnd =
        beforeStatementCount + withinStatementsDates.length;

    return {
        flatPairs,
        lastBeforeStatementIndex:
            beforeStatementCount > 0 ? beforeStatementCount - 1 : undefined,
        lastInStatementIndex:
            withinStatementsDates.length > 0 && afterClosingDate.length > 0
                ? withinStatementEnd - 1
                : undefined,
    };
}

export const RECONCILIATION_ROW_CLASSNAME = "reconciliation-row";
export const APPROVE_RECONCILIATION_CHANGE_BUTTON_CLASSNAME =
    "approve-reconciliation-change-button";

/**
 * Approximate pair-row height in px, used only as the virtualizer's
 * `estimateSize`. Rows are NOT forced to this height — they size
 * themselves from the cell padding (`--cell-padding: 12px 16px`) plus
 * content. A typical action row is ~48px (24px sm Button + 24px
 * vertical padding); stacked-date rows can reach ~64px when both old
 * and new dates render. We pick the typical height (48) rather than
 * the under-shoot value (40) so `scrollToIndex(idx, { align: "center" })`
 * lands the focused row close to the actual viewport center — an
 * under-shoot estimate makes `scrollToIndex` overshoot for large indices,
 * which is what made "Go to next difference" toggle the focused row
 * between top and below-the-viewport on alternating clicks.
 */
export const RECONCILIATION_ROW_HEIGHT_PX = 48;

export type ReconciliationPairKey = string | number;

/**
 * Stable per-pair identity used to track focus across re-orderings (e.g. when
 * pairs get bucketed into the four sections rendered by the table) and as
 * the virtualizer's `getItemKey`. Account transactions are keyed by their
 * financial transaction id; statement-only pairs fall back to their
 * statement-row composite id.
 */
export function getReconciliationPairKey(
    pair: TransactionsPairForReconciliation,
): ReconciliationPairKey | undefined {
    if (pair.fromAccount) {
        return pair.fromAccount.financialTransactionId;
    }
    if (pair.fromStatement) {
        return getStatmentTransactionId(pair.fromStatement);
    }
    return undefined;
}

export function isSamePair(
    pair1: TransactionsPairForReconciliation,
    pair2: TransactionsPairForReconciliation,
): boolean {
    const key1 = getReconciliationPairKey(pair1);
    return key1 !== undefined && key1 === getReconciliationPairKey(pair2);
}

/**
 * Find the next element in `items` (after `fromIndex`) matching `predicate`,
 * wrapping around to the start of the array if none is found after the cursor.
 * Returns `-1` if nothing in the array matches.
 */
export function findNextWrappingIndex<T>(
    items: T[],
    fromIndex: number,
    predicate: (item: T) => boolean,
): number {
    const startIndex = fromIndex < 0 ? 0 : fromIndex + 1;
    const afterCursor = items
        .slice(startIndex)
        .findIndex((item) => predicate(item));
    if (afterCursor !== -1) {
        return startIndex + afterCursor;
    }
    return items.findIndex((item) => predicate(item));
}
